'use client';

import { useMemo, useState } from 'react';
import { TIMELINE_DATA } from '@/data';
import type { HistoryType } from '@/data';

function TypeBadge({ type }: { type?: HistoryType }) {
  if (!type) return null;

  // 타입별로 살짝 톤 차이(과하지 않게)
  const base =
    'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border';

  const cls =
    type === '구축'
      ? `${base} border-emerald-200 bg-emerald-50 text-emerald-700`
      : type === '운영'
        ? `${base} border-gray-200 bg-gray-50 text-gray-600`
        : type === '고도화'
          ? `${base} border-sky-200 bg-sky-50 text-sky-700`
          : type === '컨설팅'
            ? `${base} border-purple-200 bg-purple-50 text-purple-700`
            : `${base} border-amber-200 bg-amber-50 text-amber-700`;

  return <span className={cls}>{type}</span>;
}

export default function History() {
  const [showMore, setShowMore] = useState(false);

  const sortedTimeline = useMemo(() => {
    return [...TIMELINE_DATA]
      .sort((a, b) => b.year - a.year)
      .map((year) => ({
        ...year,
        items: [...year.items].sort((a, b) => Number(b.month) - Number(a.month)),
      }));
  }, []);

  // 기본 화면: 최근 3년
  const PREVIEW_YEARS = 3;

  const visibleTimeline = useMemo(() => {
    const base = showMore ? sortedTimeline : sortedTimeline.slice(0, PREVIEW_YEARS);
    if (showMore) return base;

    return base.map((yearBlock) => {
      const count =
        yearBlock.items.length >= 4 ? 4 : Math.min(3, yearBlock.items.length);

      return {
        ...yearBlock,
        items: yearBlock.items.slice(0, count),
      };
    });
  }, [showMore, sortedTimeline]);



  return (
    <section className="py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="mb-20">
          <p className="text-emerald-500 font-semibold tracking-wide mb-2">연혁</p>
          <h2 className="text-4xl font-bold text-gray-900">History</h2>
        </div>

        {/* Timeline */}
        <div className="space-y-20">
          {visibleTimeline.map((yearBlock) => (
            <div key={yearBlock.year} className="flex gap-12">
              {/* Year */}
              <div className="w-24 flex-shrink-0">
                <span className="text-2xl font-bold text-emerald-500">
                  {yearBlock.year}
                </span>
              </div>

              <ul className="relative border-l-2 border-gray-200 pl-12 space-y-6">
                {yearBlock.items.map((item, idx) => (
                  <li key={`${yearBlock.year}-${item.month}-${idx}`}>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 -ml-12 mt-[1px] flex-shrink-0">
                        <span
                          className="
                            w-7 h-7 rounded-full
                            bg-emerald-500 text-white
                            text-[11px] font-semibold
                            flex items-center justify-center
                          "
                          aria-label={`${item.month}월`}
                        >
                          {item.month}
                        </span>
                        <TypeBadge type={item.type} />
                      </div>

                      <p className="text-gray-800 leading-7">
                        {item.content}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Toggle Button */}
        <div className="mt-20 text-center">
          <button
            onClick={() => setShowMore((prev) => !prev)}
            className="
              px-8 py-3
              border border-gray-300
              rounded-full
              text-sm font-medium
              text-gray-700
              hover:bg-gray-50
              transition
            "
          >
            {showMore ? '연혁 접기' : '전체 연혁 보기'}
          </button>
        </div>
      </div>
    </section>
  );
}
